import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { Label } from "@/components/ui/Label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { useAuth } from "@/contexts/AuthContext";
import { ticketsApi, ApiError } from "@/lib/api";
import {
  NoApiKeyBanner,
  NoApiKeyDialog,
  useApiKeyStatus,
} from "../components/ApiKeyGate";

const schema = z.object({
  subject: z.string().min(3, "Subject required"),
  description: z.string().min(3, "Description required"),
  priority: z.enum(["P1", "P2", "P3", "P4"]),
  customer_email: z.string().email("Invalid email").optional().or(z.literal("")),
});
type FormValues = z.infer<typeof schema>;

export function TenantTicketNew({ tenantCode }: { tenantCode: string }) {
  const { idToken } = useAuth();
  const navigate = useNavigate();
  const { hasActiveKey, isResolved } = useApiKeyStatus(tenantCode);
  const [gateOpen, setGateOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { subject: "", description: "", priority: "P3", customer_email: "" },
  });

  const onSubmit = async (values: FormValues) => {
    if (!idToken) return;
    // Surface the no-api-key gate at submit time (not on mount), so a user
    // who's just exploring the form can read it; the gate fires only when
    // they commit to creating a ticket.
    if (isResolved && !hasActiveKey) {
      setGateOpen(true);
      return;
    }
    try {
      const ticket = await ticketsApi.create(
        tenantCode,
        {
          subject: values.subject,
          description: values.description,
          priority: values.priority,
          customer_email: values.customer_email || undefined,
        },
        idToken,
      );
      toast.success("Ticket created");
      navigate(`/support/${ticket.id}`);
    } catch (err) {
      const ae = err as ApiError;
      toast.error("Couldn't create ticket", { description: ae.message });
    }
  };

  return (
    <div className="p-8 max-w-2xl space-y-4">
      <NoApiKeyBanner tenantCode={tenantCode} />
      <NoApiKeyDialog open={gateOpen} onOpenChange={setGateOpen} />
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4">
        <ArrowLeft className="h-4 w-4" />
        Back
      </Button>
      <Card>
        <CardHeader>
          <CardTitle>New ticket</CardTitle>
          <CardDescription>Open a new ticket for your team or an agent to pick up.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
            <div className="space-y-1.5">
              <Label>Subject</Label>
              <Input {...form.register("subject")} />
              {form.formState.errors.subject?.message && (
                <p className="text-xs text-signal-failed">{form.formState.errors.subject.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea rows={6} {...form.register("description")} />
              {form.formState.errors.description?.message && (
                <p className="text-xs text-signal-failed">{form.formState.errors.description.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Select
                  value={form.watch("priority")}
                  onValueChange={(v) => form.setValue("priority", v as FormValues["priority"])}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="P1">P1 — Urgent</SelectItem>
                    <SelectItem value="P2">P2 — High</SelectItem>
                    <SelectItem value="P3">P3 — Medium</SelectItem>
                    <SelectItem value="P4">P4 — Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Customer email (optional)</Label>
                <Input type="email" placeholder="customer@example.com" {...form.register("customer_email")} />
                {form.formState.errors.customer_email?.message && (
                  <p className="text-xs text-signal-failed">{form.formState.errors.customer_email.message}</p>
                )}
              </div>
            </div>

            <Button type="submit" size="lg" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Create ticket
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
